import React from 'react';
import './DataTable.css';

/**
 * Declarative data table: pass column definitions and rows.
 *
 * @typedef {object} DataTableColumn
 * @property {string} id — stable key for header/cell cells
 * @property {React.ReactNode} header — label or custom header content
 * @property {(row: object, rowIndex: number) => React.ReactNode} cell
 * @property {React.ThHTMLAttributes<HTMLTableCellElement>} [headerProps] — e.g. className, onClick for sortable headers
 * @property {(row: object, rowIndex: number) => React.TdHTMLAttributes<HTMLTableCellElement>} [tdProps] — per-cell td attributes
 */

export default function DataTable({
  columns,
  rows,
  getRowKey = (row, i) => row?.id ?? i,
  onRowClick,
  tableClassName = '',
  emptyMessage = 'No records found',
  emptyCellClassName = '',
  'aria-label': ariaLabel,
}) {
  const colCount = columns.length;
  const tableClasses = ['data-table-layout', tableClassName].filter(Boolean).join(' ');

  return (
    <table className={tableClasses} role="grid" aria-label={ariaLabel}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.id} scope="col" {...(col.headerProps || {})}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={colCount} className={emptyCellClassName}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row, rowIndex) => {
            const rowKey = String(getRowKey(row, rowIndex));
            const rowProps = onRowClick
              ? {
                  className: 'data-table-row-clickable',
                  role: 'button',
                  tabIndex: 0,
                  onClick: () => onRowClick(row, rowIndex),
                  onKeyDown: (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(row, rowIndex);
                    }
                  },
                }
              : {};

            return (
              <tr key={rowKey} {...rowProps}>
                {columns.map((col) => (
                  <td key={col.id} {...(col.tdProps?.(row, rowIndex) || {})}>
                    {col.cell(row, rowIndex)}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}
